import { getDeviceId as getDeviceIdAmplitude } from '@amplitude/analytics-browser'
import { Experiment, ExperimentConfig, ExperimentUser, Exposure, Variant } from '@amplitude/experiment-js-client'

import helpers from '@lib/analytics/amplitude/helpers'

export interface ExperimentVariant {
  value: string
  payload?: Record<string, any>
}

const getExperimentConfig = (): ExperimentConfig => ({
  automaticExposureTracking: true,
  fetchOnStart: true,
  userProvider: {
    getUser: (): ExperimentUser => {
      const deviceId = getDeviceIdAmplitude()
      return {
        device_id: deviceId || '',
        user_properties: {},
      }
    },
  },
  exposureTrackingProvider: {
    track: (exposure: Exposure) => {
      helpers.sendEvent('experiment_exposure', {
        flag_key: exposure.flag_key,
        variant: exposure.variant,
        timestamp: Date.now(),
      })
    },
  },
})

let experimentInstance: ReturnType<typeof Experiment.initializeWithAmplitudeAnalytics> | null = null

export const init = async ({ amplitudeApiKey }: { amplitudeApiKey: string }): Promise<void> => {
  if (experimentInstance) {
    return
  }

  const deploymentKey = amplitudeApiKey

  if (!deploymentKey) {
    return
  }

  // Add a small delay to ensure Amplitude Analytics is initialized
  await new Promise(resolve => setTimeout(resolve, 100))

  experimentInstance = Experiment.initializeWithAmplitudeAnalytics(deploymentKey, getExperimentConfig())

  await experimentInstance.fetch()
}

export const getExperimentInstance = () => {
  if (!experimentInstance) {
    throw new Error('Experiments not initialized. Call initializeExperiments() first.')
  }
  return experimentInstance
}

export const getVariant = (flagKey: string, fallback?: string | Variant): ExperimentVariant => {
  const experiment = getExperimentInstance()
  const variant = experiment.variant(flagKey, fallback)

  return {
    value: variant.value ?? '',
    payload: variant.payload,
  }
}

export const getFlagValue = (flagKey: string, fallback: string = ''): string => {
  const variant = getVariant(flagKey, fallback)
  return variant.value
}

export const getFlagPayload = (flagKey: string): Record<string, any> | null => {
  const variant = getVariant(flagKey)
  return variant.payload || null
}

export default {
  init,
  getExperimentInstance,
  getVariant,
  getFlagValue,
  getFlagPayload,
}
